import { JSX } from "react";
import { ProductDetailInfoBar } from "@hatch-digital-nl/bravilor-storybook";
import type { ProductCardButtonVariant } from "@hatch-digital-nl/bravilor-storybook";
import { LanguageInterface } from "../../../common/interfaces/shared/LanguageInterface";
import { RefurbishedProductDetailInterface } from "../../../common/interfaces/apiV2/refurbished/RefurbishedProductDetailInterface";
import { createMailtoUrl } from "../../../common/utils/productInforBarTransformer";
import { ReactComponent as CheckRefurbishedSvg } from "../../../common/svgs/check-refurbished.svg";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";

interface InfoBarLabels {
    serialNumber: string;
    identifier: string;
    refurbished: string;
    requestButton: string;
    downloadButton: string;
    priceTooltipTitle: string;
    priceTooltipSubtitle: string;
}

interface Props {
    data: RefurbishedProductDetailInterface | undefined;
    isPageLoading: boolean;
    activeLanguage: LanguageInterface | null;
    productName: string;
    pricing: { current: string; original?: string };
    categoryBreadcrumbs: { label: string }[];
    labels: InfoBarLabels;
    reserveButtonLabel: string;
    reserveButtonVariant: ProductCardButtonVariant;
    canReserve: boolean;
    isDownloading: boolean;
    isReservingProduct: (productId: number) => boolean;
    communicationEmail: string | null;
    onDownload: (data: RefurbishedProductDetailInterface) => void;
    onReserve: (productId: number) => void;
}

export const RefurbishedProductDetailsInfoBar = ({
    data,
    isPageLoading,
    activeLanguage,
    productName,
    pricing,
    categoryBreadcrumbs,
    labels,
    reserveButtonLabel,
    reserveButtonVariant,
    canReserve,
    isDownloading,
    isReservingProduct,
    communicationEmail,
    onDownload,
    onReserve,
}: Props): JSX.Element => {
    const { t } = useStringTranslation();

    if (isPageLoading || !data || !activeLanguage) {
        return <ProductDetailInfoBar variant="refurbished" isLoading />;
    }

    const mailTo = communicationEmail
        ? createMailtoUrl(
              communicationEmail,
              t("request-information-subject"),
              productName,
          )
        : "";

    return (
        <ProductDetailInfoBar
            variant="refurbished"
            name={productName}
            identifier={data.identifier}
            serialNumber={data.serial_number}
            serialNumberLabel={labels.serialNumber}
            identifierLabel={labels.identifier}
            price={pricing.current}
            originalPrice={pricing.original}
            priceLabel={data.price?.label}
            refurbishedTagLabel={labels.refurbished}
            grade={data.grade}
            breadcrumbs={categoryBreadcrumbs}
            showButtons
            requestButtonLabel={labels.requestButton}
            downloadButtonLabel={labels.downloadButton}
            reserveButtonLabel={reserveButtonLabel}
            reserveButtonVariant={reserveButtonVariant}
            reserveButtonIcon={
                reserveButtonVariant === "reserved"
                    ? CheckRefurbishedSvg
                    : undefined
            }
            isDownloading={isDownloading}
            isReserveLoading={isReservingProduct(data.refurbished_product_id)}
            priceTooltip={{
                title: labels.priceTooltipTitle,
                subtitle: labels.priceTooltipSubtitle,
            }}
            onRequestClick={
                communicationEmail
                    ? () => {
                          window.location.href = mailTo;
                      }
                    : undefined
            }
            onDownloadClick={() => onDownload(data)}
            onReserveClick={
                canReserve
                    ? () => void onReserve(data.refurbished_product_id)
                    : undefined
            }
        />
    );
};
